'use server'

import { apiRequest, ApiResponse } from '../request'

export type TissTransactionType =
    | 'ELIGIBILITY'
    | 'AUTHORIZATION'
    | 'GUIDE_SUBMISSION'
    | 'GUIDE_STATUS'
    | 'PAYMENT_QUERY'
    | 'BATCH_SUBMISSION'

export type TissGuideType =
    | 'CONSULTA'
    | 'SADT'
    | 'INTERNACAO'
    | 'HONORARIOS'
    | 'ANEXO_OPME'
    | 'ANEXO_QUIMIO'

export type TissStatus =
    | 'PENDING'
    | 'SUBMITTED'
    | 'ACCEPTED'
    | 'REJECTED'
    | 'PARTIALLY_ACCEPTED'
    | 'CANCELLED'

export interface TissConfiguration {
    id: number
    companyId: number
    name: string
    ansCode: string
    cnes: string
    cnpj: string
    active: boolean
    environment: 'PRODUCTION' | 'HOMOLOGATION'
    certificatePath?: string
    certificatePassword?: string
    tissVersion: string
    settings?: any
    createdAt: string
    updatedAt: string
    _count?: {
        credentials: number
        transactions: number
    }
}

/**
 * Credenciais de transmissão TISS de um convênio nesta filial.
 *
 * Quem é a operadora (nome, registro ANS) vem do convênio em `healthPlan` — este
 * registro guarda só o acesso ao webservice. Não confundir com o cadastro do
 * convênio, que é org-global e vive em /medical/health-plans.
 */
export interface TissCredential {
    id: number
    companyId: number
    healthPlanId: number
    healthPlan?: {
        id: number
        name: string
        ansCode?: string
    }
    webServiceUrl?: string
    username?: string
    settings?: any
    active: boolean
    createdAt: string
    updatedAt: string
    _count?: {
        transactions: number
    }
}

export interface TissTransaction {
    id: number
    companyId: number
    credentialId: number
    credential?: {
        id: number
        healthPlan?: {
            id: number
            name: string
            ansCode?: string
        }
    }
    /** Guia de faturamento que originou a transmissão (ausente em elegibilidade/autorização). */
    guideId?: number
    guide?: {
        id: number
        guideNumber: string
        status: string
    }
    clientId?: number
    client?: {
        id: number
        name: string
        cpf?: string
    }
    type: TissTransactionType
    guideType?: TissGuideType
    guideNumber?: string
    requestXml?: string
    responseXml?: string
    status: TissStatus
    protocolNumber?: string
    authorizationCode?: string
    denialReason?: string
    requestedValue?: number
    authorizedValue?: number
    sentAt?: string
    respondedAt?: string
    metadata?: any
    createdAt: string
    updatedAt: string
}

export interface CreateTissConfigurationData {
    name: string
    ansCode: string
    cnes: string
    cnpj: string
    active?: boolean
    environment?: 'PRODUCTION' | 'HOMOLOGATION'
    tissVersion?: string
    settings?: any
}

export interface UpdateTissConfigurationData
    extends Partial<CreateTissConfigurationData> {}

export interface CreateTissCredentialData {
    healthPlanId: number
    webServiceUrl?: string
    username?: string
    password?: string
    settings?: any
}

export interface UpdateTissCredentialData
    extends Partial<Omit<CreateTissCredentialData, 'healthPlanId'>> {
    active?: boolean
}

export interface EligibilityRequestData {
    credentialId: number
    beneficiaryCard: string
    beneficiaryName?: string
}

export interface AuthorizationRequestData {
    credentialId: number
    beneficiaryCard: string
    beneficiaryName: string
    guideType: TissGuideType
    orders: Array<{
        code: string
        description: string
        quantity: number
    }>
}

export interface TissStats {
    totalConfigurations: number
    totalCredentials: number
    totalTransactions: number
    byStatus: Record<TissStatus, number>
    byType: Record<TissTransactionType, number>
}

// Configuration CRUD
export async function getTissConfigurations(): Promise<
    ApiResponse<TissConfiguration[]>
> {
    return apiRequest<TissConfiguration[]>('tiss/configurations', {})
}

export async function getTissConfigurationsByCompany(
    companyId: number
): Promise<ApiResponse<TissConfiguration[]>> {
    return apiRequest<TissConfiguration[]>(
        `tiss/configurations/company/${companyId}`,
        {}
    )
}

export async function getTissConfiguration(
    id: number
): Promise<ApiResponse<TissConfiguration>> {
    return apiRequest<TissConfiguration>(`tiss/configurations/${id}`, {})
}

export async function createTissConfiguration(
    data: CreateTissConfigurationData
): Promise<ApiResponse<TissConfiguration>> {
    return apiRequest<TissConfiguration>(
        'tiss/configurations',
        { data },
        'post'
    )
}

export async function updateTissConfiguration(
    id: number,
    data: UpdateTissConfigurationData
): Promise<ApiResponse<TissConfiguration>> {
    return apiRequest<TissConfiguration>(
        `tiss/configurations/${id}`,
        { data },
        'put'
    )
}

export async function deleteTissConfiguration(
    id: number
): Promise<ApiResponse<void>> {
    return apiRequest<void>(`tiss/configurations/${id}`, {}, 'delete')
}

// Credenciais de transmissão (CRUD)
export async function getTissCredentials(): Promise<
    ApiResponse<TissCredential[]>
> {
    return apiRequest<TissCredential[]>('tiss/credentials', {})
}

export async function getTissCredential(
    id: number
): Promise<ApiResponse<TissCredential>> {
    return apiRequest<TissCredential>(`tiss/credentials/${id}`, {})
}

export async function createTissCredential(
    data: CreateTissCredentialData
): Promise<ApiResponse<TissCredential>> {
    return apiRequest<TissCredential>('tiss/credentials', { data }, 'post')
}

export async function updateTissCredential(
    id: number,
    data: UpdateTissCredentialData
): Promise<ApiResponse<TissCredential>> {
    return apiRequest<TissCredential>(`tiss/credentials/${id}`, { data }, 'put')
}

export async function deleteTissCredential(
    id: number
): Promise<ApiResponse<void>> {
    return apiRequest<void>(`tiss/credentials/${id}`, {}, 'delete')
}

// Transactions
export async function getTissTransactions(filters?: {
    credentialId?: number
    /** Filtra pelo convênio, resolvido através da credencial usada na transmissão. */
    healthPlanId?: number
    guideId?: number
    clientId?: number
    type?: TissTransactionType
    guideType?: TissGuideType
    status?: TissStatus
    startDate?: string
    endDate?: string
}): Promise<ApiResponse<TissTransaction[]>> {
    return apiRequest<TissTransaction[]>('tiss/transactions', {
        params: filters,
    })
}

export async function getTissTransaction(
    id: number
): Promise<ApiResponse<TissTransaction>> {
    return apiRequest<TissTransaction>(`tiss/transactions/${id}`, {})
}

// TISS Operations
export async function checkEligibility(
    data: EligibilityRequestData
): Promise<ApiResponse<TissTransaction>> {
    return apiRequest<TissTransaction>(
        'tiss/transactions/eligibility',
        { data },
        'post'
    )
}

export async function requestAuthorization(
    data: AuthorizationRequestData
): Promise<ApiResponse<TissTransaction>> {
    return apiRequest<TissTransaction>(
        'tiss/transactions/authorization',
        { data },
        'post'
    )
}

export async function getTissStats(): Promise<ApiResponse<TissStats>> {
    return apiRequest<TissStats>('tiss/stats', {})
}

export const TISS_STATUS_LABELS: Record<TissStatus, string> = {
    PENDING: 'Pendente',
    SUBMITTED: 'Enviada',
    ACCEPTED: 'Aceita',
    REJECTED: 'Rejeitada',
    PARTIALLY_ACCEPTED: 'Parcialmente Aceita',
    CANCELLED: 'Cancelada',
}

export const TISS_STATUS_COLORS: Record<TissStatus, string> = {
    PENDING: 'badge-warning',
    SUBMITTED: 'badge-info',
    ACCEPTED: 'badge-success',
    REJECTED: 'badge-error',
    PARTIALLY_ACCEPTED: 'badge-warning',
    CANCELLED: 'badge-ghost',
}

export const TISS_TRANSACTION_TYPE_LABELS: Record<TissTransactionType, string> =
    {
        ELIGIBILITY: 'Elegibilidade',
        AUTHORIZATION: 'Autorizacao',
        GUIDE_SUBMISSION: 'Envio de Guia',
        GUIDE_STATUS: 'Status da Guia',
        PAYMENT_QUERY: 'Consulta Pagamento',
        BATCH_SUBMISSION: 'Lote',
    }

export const TISS_GUIDE_TYPE_LABELS: Record<TissGuideType, string> = {
    CONSULTA: 'Consulta',
    SADT: 'SP/SADT',
    INTERNACAO: 'Internacao',
    HONORARIOS: 'Honorarios',
    ANEXO_OPME: 'Anexo OPME',
    ANEXO_QUIMIO: 'Anexo Quimioterapia',
}
